import React, { useRef } from 'react'
import { ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline'
import Modal from '../Modal'

interface ConfirmationModalProps {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  title: string
  message: React.ReactNode
  confirmLabel?: string
  cancelLabel?: string
  onConfirm: () => void
  variant?: 'danger' | 'info'
}

const variantConfig = {
  danger: {
    Icon: ExclamationTriangleIcon,
    iconBg: 'bg-cerise-red-100',
    iconColor: 'text-cerise-red-600',
    buttonClass: 'bg-cerise-red hover:bg-cerise-red-700 focus:ring-cerise-red',
  },
  info: {
    Icon: InformationCircleIcon,
    iconBg: 'bg-perfect-shine-100',
    iconColor: 'text-perfect-shine-600',
    buttonClass: 'bg-perfect-shine hover:bg-perfect-shine-dark focus:ring-perfect-shine',
  },
}

const ConfirmationModal = ({
  isOpen,
  setIsOpen,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  variant = 'danger',
}: ConfirmationModalProps) => {
  const cancelButtonRef = useRef<HTMLButtonElement>(null)
  const config = variantConfig[variant]

  return (
    <Modal isOpen={isOpen} setIsOpen={setIsOpen} initialFocus={cancelButtonRef}>
      <div className="sm:flex sm:items-start">
        <div
          className={`mx-auto shrink-0 flex items-center justify-center h-12 w-12 rounded-full ${config.iconBg} sm:mx-0 sm:h-10 sm:w-10`}>
          <config.Icon className={`h-6 w-6 ${config.iconColor}`} aria-hidden="true" />
        </div>
        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mt-2">
            {title}
          </h3>
          <div className="mt-2 text-sm text-gray-500 space-y-4 py-2">
            {message}
          </div>
        </div>
      </div>
      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
        <button
          type="button"
          className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-xs px-4 py-2 text-base font-medium text-white focus:outline-hidden focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm ${config.buttonClass}`}
          onClick={onConfirm}
        >
          {confirmLabel}
        </button>
        <button
          type="button"
          className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-xs px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-perfect-shine sm:mt-0 sm:w-auto sm:text-sm"
          onClick={() => setIsOpen(false)}
          ref={cancelButtonRef}
        >
          {cancelLabel}
        </button>
      </div>
    </Modal>
  )
}

export default ConfirmationModal
